import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef, useState, type FC } from "react";

import { getClassicOptions } from "../../../api/drafts-v2";
import type { ClassicOptions } from "../../../api/types/classicDrafting";
import type { Draft } from "../../../api/types/draft";
import { useCaseIde } from "../context/CaseIdeContext";
import { WorkspaceTreeAPI } from "../workspace/api/workspaceTreeApi";
import { draftTitle } from "../workspace/workspaceModel";
import { CaseStrategyStep } from "./CaseStrategyStep";
import { ClassicChooser } from "./ClassicChooser";
import { ClassicRunPane } from "./ClassicRunPane";
import { defaultPick, type ClassicPick } from "./classicModel";

const RESEARCH_RELOAD_MS = 1500;

type Step = "choose" | "strategy" | "started";

type Props = {
  draft: Draft;
  /** The attorney picked Casey: the pane goes back to the editor. */
  onChooseCasey: () => void;
};

/**
 * An unwritten draft at a Classic firm: the chooser, then the Case Strategy,
 * then the run (until the draft list reports it).
 */
export const ClassicStart: FC<Props> = ({ draft, onChooseCasey }) => {
  const { requestPrompt } = useCaseIde();
  const classicAvailable = draft.classic_available === true;
  const [step, setStep] = useState<Step>("choose");
  const [options, setOptions] = useState<ClassicOptions | null>(null);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [pick, setPick] = useState<ClassicPick | null>(null);
  const reloadTimer = useRef<number | null>(null);

  const loadOptions = useCallback(async () => {
    try {
      const next = await getClassicOptions(draft.draft_id);
      setOptions(next);
      setOptionsError(null);
      setPick((current) => current ?? defaultPick(next));
    } catch (error) {
      Sentry.captureException(error);
      setOptionsError(
        "Couldn't load the classic templates. Try again shortly."
      );
    }
  }, [draft.draft_id]);

  useEffect(() => {
    if (classicAvailable) void loadOptions();
  }, [classicAvailable, loadOptions]);

  const reloadResearch = useCallback(() => {
    if (reloadTimer.current) window.clearTimeout(reloadTimer.current);
    reloadTimer.current = window.setTimeout(() => {
      reloadTimer.current = null;
      void loadOptions();
    }, RESEARCH_RELOAD_MS);
  }, [loadOptions]);
  useEffect(
    () => () => {
      if (reloadTimer.current) window.clearTimeout(reloadTimer.current);
    },
    []
  );

  const startInChat = useCallback(async () => {
    let prompt: string | null = null;
    try {
      const { draft_types } = await WorkspaceTreeAPI.getDraftTypes(
        draft.individual_id
      );
      prompt =
        draft_types.find((t) => t.draft_type === draft.draft_type)
          ?.intake_prompt ?? null;
    } catch (error) {
      Sentry.captureException(error);
    }
    requestPrompt(prompt ?? `Draft the ${draftTitle(draft)}.`);
    onChooseCasey();
  }, [draft, requestPrompt, onChooseCasey]);

  if (step === "started") return <ClassicRunPane draft={undefined} />;
  if (step === "strategy" && pick) {
    return (
      <CaseStrategyStep
        draft={draft}
        pick={pick}
        research={options?.research ?? []}
        onAnswersSaved={reloadResearch}
        onBack={() => setStep("choose")}
        onStarted={() => setStep("started")}
      />
    );
  }
  return (
    <ClassicChooser
      classicAvailable={classicAvailable}
      options={options}
      optionsError={optionsError}
      pick={pick}
      onPickChange={setPick}
      onContinue={() => setStep("strategy")}
      onStartInChat={() => void startInChat()}
    />
  );
};
